/**
 * The drill-down drawer. Any card or row with a drill spec opens here.
 *
 * Each level is a table of what the number above it is made of. A row that
 * breaks down further is a button; pressing it pushes the next level, and the
 * breadcrumb walks back up. The last level is the ledger lines themselves —
 * and, for a loom, each roll.
 *
 * One drawer for the whole application, provided once by the shell, so a
 * card on any screen opens the same thing the same way.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "../lib/api";
import { controls, type DrillLevel, type DrillSpec } from "../lib/controls";
import { fmtDate, fmtQty } from "../lib/format";
import { useSheet, type SheetColumn } from "../lib/sheet";
import { SheetHead, SheetToolbar } from "./Sheet";

type Row = DrillLevel["rows"][number];

const DrillContext = createContext<(spec: DrillSpec) => void>(() => undefined);

/** Open a breakdown from anywhere below the shell. */
export function useDrill(): (spec: DrillSpec) => void {
  return useContext(DrillContext);
}

export function DrillProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<DrillSpec[]>([]);
  const open = useCallback((spec: DrillSpec) => setStack([spec]), []);

  return (
    <DrillContext.Provider value={open}>
      {children}
      {stack.length > 0 && (
        <DrillDrawer
          stack={stack}
          onPush={(spec) => setStack((s) => [...s, spec])}
          onBack={(depth) => setStack((s) => s.slice(0, depth + 1))}
          onClose={() => setStack([])}
        />
      )}
    </DrillContext.Provider>
  );
}

/** What a totals key is called when no column on this level names it. */
const TOTAL_LABELS: Record<string, string> = {
  runs: "Entries", hours: "Hours", manpower: "Manpower", kwh: "Power kWh",
  input: "Input kg", output: "Output kg", loss: "Waste kg",
  variance: "Unaccounted kg", variance_pct: "Unaccounted %",
  lines: "Lines", qty: "Quantity", shifts: "Loom shifts",
  input_kg: "Input kg", output_kg: "Output kg", efficiency_pct: "Efficiency %",
};

function display(value: string | number | null | undefined, numeric: boolean,
                 key: string): string {
  if (value === null || value === undefined || value === "") return "—";
  if (!numeric) {
    const text = String(value);
    return /^\d{4}-\d{2}-\d{2}$/.test(text) ? fmtDate(text) : text;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const decimals = /pct|ratio|per_bag/.test(key) ? 2
    : Number.isInteger(n) ? 0 : 2;
  return fmtQty(n, decimals);
}

function DrillDrawer({
  stack,
  onPush,
  onBack,
  onClose,
}: {
  stack: DrillSpec[];
  onPush: (spec: DrillSpec) => void;
  onBack: (depth: number) => void;
  onClose: () => void;
}) {
  const spec = stack[stack.length - 1];
  const [level, setLevel] = useState<DrillLevel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    setLevel(null);
    setError(null);
    controls
      .drill(spec)
      .then((l) => alive && setLevel(l))
      .catch((e: ApiError) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [spec]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (stack.length > 1) onBack(stack.length - 2);
      else onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [stack.length, onBack, onClose]);

  useEffect(() => panel.current?.focus(), []);

  const columns = useMemo<SheetColumn<Row>[]>(
    () =>
      (level?.columns ?? []).map((c) => ({
        key: c.key,
        label: c.label,
        numeric: c.numeric,
        type: c.numeric ? "number" : c.key === "date" ? "date" : "text",
        value: (r: Row) => {
          const v = r.cells[c.key];
          return c.numeric && v !== null && v !== undefined ? Number(v) : v;
        },
        format: (r: Row) => display(r.cells[c.key], c.numeric, c.key),
      })),
    [level]
  );
  const sheet = useSheet(level?.rows ?? [], columns);
  const crumbs = spec.crumbs ?? [];
  const exportName = (crumbs.join("-") || "breakdown")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");

  return (
    <div className="drill" role="dialog" aria-modal="true"
         aria-label="What this number is made of">
      <div className="drill__scrim" onClick={onClose} aria-hidden="true" />
      <div className="drill__panel" ref={panel} tabIndex={-1}>
        <header className="drill__head">
          <nav className="drill__crumbs" aria-label="Breakdown levels">
            {crumbs.map((crumb, i) => {
              // The crumbs of the current spec name every level so far; each
              // stack entry is one of them, the rest are the grouping labels.
              const depth = i - (crumbs.length - stack.length);
              const last = i === crumbs.length - 1;
              return (
                <span key={`${crumb}-${i}`} className="drill__crumb">
                  {i > 0 && <span aria-hidden="true">›</span>}
                  {!last && depth >= 0 ? (
                    <button type="button" onClick={() => onBack(depth)}>
                      {crumb}
                    </button>
                  ) : (
                    <span className={last ? "drill__here" : undefined}>
                      {crumb}
                    </span>
                  )}
                </span>
              );
            })}
          </nav>
          <div className="drill__actions">
            {stack.length > 1 && (
              <button className="btn btn--ghost btn--auto btn--sm"
                      onClick={() => onBack(stack.length - 2)}>
                ← Back
              </button>
            )}
            <button className="btn btn--ghost btn--auto btn--sm"
                    onClick={onClose} aria-label="Close breakdown">
              Close
            </button>
          </div>
        </header>

        {error && <div className="banner banner--negative">{error}</div>}

        {!level && !error && (
          <div className="state">
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton state__bar" />
            ))}
          </div>
        )}

        {level && (
          <>
            <h3 className="drill__title">{level.title}</h3>
            {level.note && <p className="section-note">{level.note}</p>}

            {Object.keys(level.totals).length > 0 && (
              <dl className="drill__totals">
                {Object.entries(level.totals).map(([key, value]) => (
                  <div key={key}>
                    <dt>
                      {level.columns.find((c) => c.key === key)?.label ??
                        TOTAL_LABELS[key] ?? key.replace(/_/g, " ")}
                    </dt>
                    <dd className="num mono">
                      {display(value, typeof value !== "string" ||
                        !Number.isNaN(Number(value)), key)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {level.rows.length === 0 ? (
              <div className="state state--empty">
                <p>Nothing here for this period.</p>
              </div>
            ) : (
              <>
                <SheetToolbar sheet={sheet} exportName={exportName}>
                  <span className="page__note">
                    {level.rows.some((r) => r.drill)
                      ? "Press a row to see what it is made of."
                      : "This is the last level."}
                  </span>
                </SheetToolbar>
                <div className="tablewrap">
                  <table className="table sheet">
                    <SheetHead sheet={sheet} trailing={<th aria-label="Open" />} />
                    <tbody>
                      {sheet.rows.map((row, index) => (
                        <tr
                          key={index}
                          className={row.drill ? "drill__row" : undefined}
                          onClick={row.drill ? () => onPush(row.drill!) : undefined}
                          onKeyDown={(e) => {
                            if (row.drill && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              onPush(row.drill);
                            }
                          }}
                          tabIndex={row.drill ? 0 : undefined}
                        >
                          {level.columns.map((c) => (
                            <td key={c.key}
                                className={c.numeric ? "num mono" : undefined}>
                              {display(row.cells[c.key], c.numeric, c.key)}
                            </td>
                          ))}
                          <td className="drill__chevron" aria-hidden="true">
                            {row.drill ? "›" : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** A number on a card that opens its breakdown when it has one. */
export function DrillCard({
  drill,
  children,
  className = "kpi",
}: {
  drill?: DrillSpec | null;
  children: ReactNode;
  className?: string;
}) {
  const open = useDrill();
  if (!drill) return <div className={className}>{children}</div>;
  return (
    <button
      type="button"
      className={`${className} kpi--drill`}
      onClick={() => open(drill)}
      title="See what this number is made of"
    >
      {children}
      <span className="kpi__open" aria-hidden="true">Breakdown ›</span>
    </button>
  );
}
